import React, { useCallback, useMemo } from 'react';
import { FieldValues, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, paperClasses, Stack, Typography } from '@mui/material';
import { matchIsValidTel } from 'mui-tel-input';
import * as yup from 'yup';

import { MidotUserAssignmentRequest, useAssignUsers, UserIdentifier } from '@/api/generated';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import PlusCircleIcon from '@/assets/icons/plus-circle.svg?react';
import Field from '@/components/fields/Field';
import { CommonFormFooter } from '@/components/Form/Form';
import { useSnackbar } from '@/hooks/useSnackbar';
import typography from '@/theme/typography';
import InviteUserRow from '@/views/fields/InviteUserRow';
import OfficeDropdownSelect from '@/views/fields/OfficeDropdownSelect/OfficeDropdownSelect';
import RoleDropdownSelect from '@/views/fields/RoleDropdownSelect/RoleDropdownSelect';
import { getMidotUserDropdownSelect } from '@/views/fields/UserDropdownSelect/MidotUserDropdownSelect';
import SettingsModalForm from '@/views/SettingsModalForm/SettingsModalForm';

interface InviteMidotUsersModalProps {
  onClose: () => void;
}

interface InviteMidotUsersFormData {
  assignments: {
    role?: string;
    office: string[];
    user?: UserIdentifier;
    phone?: string;
  }[];
}

const ALLOWED_EMAIL_DOMAINS = ['midot.com', 'midot.co.il', 'midot.com.mx', 'webiya.com', 'webiya.co.il'];

const isAllowedEmailDomain = (email: string): boolean => {
  const domain = email.toLowerCase().split('@').pop();

  return ALLOWED_EMAIL_DOMAINS.some(allowed => domain === allowed);
};

const DISABLED_IDS_SEPARATOR = ',';

const InviteMidotUsersRow: React.FC<{ index: number }> = ({ index }) => {
  const { t } = useTranslation(['users', 'common']);
  const { control } = useFormContext<InviteMidotUsersFormData>();
  const assignments = useWatch({ control, name: 'assignments' });

  const otherIdsSignature = useMemo(() => {
    const list = assignments ?? [];
    const otherIds = list
      .filter((_, i) => i !== index)
      .map(a => a?.user?.id || a?.user?.email)
      .filter(Boolean) as string[];

    return otherIds.join(DISABLED_IDS_SEPARATOR);
  }, [assignments, index]);

  const disabledOptions = useMemo(
    () => (otherIdsSignature ? otherIdsSignature.split(DISABLED_IDS_SEPARATOR) : []),
    [otherIdsSignature]
  );

  const midotUserDropdownSelect = useMemo(
    () =>
      getMidotUserDropdownSelect({
        disabledOptions
      }) as React.ComponentType<Record<string, unknown>>,
    [disabledOptions]
  );

  return (
    <>
      <Field
        component={OfficeDropdownSelect}
        name={`assignments.${index}.office`}
        innerProps={{
          isMulti: true,
          usePortal: true,
          placeholder: t('users.inviteMidotUsers.selectOffice')
        }}
      />
      <Field
        component={RoleDropdownSelect}
        name={`assignments.${index}.role`}
        innerProps={{
          isMulti: false,
          usePortal: true,
          placeholder: t('users.inviteMidotUsers.selectRole')
        }}
      />
      <InviteUserRow
        name={`assignments.${index}`}
        userDropdownSelect={midotUserDropdownSelect}
        placeholder={t('users.inviteMidotUsers.selectUser')}
      />
    </>
  );
};

const InviteMidotUsersTable: React.FC = () => {
  const { t } = useTranslation(['users', 'common']);
  const { control } = useFormContext<InviteMidotUsersFormData>();
  const {
    fields: assignments,
    append: onAddAssignment,
    remove: onRemoveAssignment
  } = useFieldArray({
    control,
    name: 'assignments'
  });

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 32px', width: '100%', gap: 0.5, alignItems: 'start' }}
    >
      <Typography variant="body3" fontWeight={typography.fontWeightMedium} color="grey.700">
        {t('users.inviteMidotUsers.office')}
      </Typography>
      <Typography variant="body3" fontWeight={typography.fontWeightMedium} color="grey.700">
        {t('users.inviteMidotUsers.role')}
      </Typography>
      <Typography variant="body3" fontWeight={typography.fontWeightMedium} color="grey.700">
        {t('users.inviteMidotUsers.user')}
      </Typography>
      <Box />
      {assignments.map((_field, index: number) => (
        <React.Fragment key={_field.id}>
          <InviteMidotUsersRow index={index} />
          <IconButton onClick={() => onRemoveAssignment(index)} sx={{ mt: 0.5 }}>
            <DeleteIcon />
          </IconButton>
        </React.Fragment>
      ))}

      <Button
        startIcon={<PlusCircleIcon />}
        sx={{
          marginInlineStart: -1,
          textAlign: 'start',
          justifyContent: 'start',
          alignSelf: 'start',
          ...typography.body3,
          fontWeight: typography.fontWeightMedium,
          '& .MuiButton-icon': { mr: 0.5 }
        }}
        onClick={() => onAddAssignment({ role: undefined, office: [], user: undefined, phone: '' })}
      >
        {t('users.inviteMidotUsers.addAnother')}
      </Button>
    </Box>
  );
};

interface InviteMidotUsersModalContentProps {
  onClose: () => void;
  isLoading: boolean;
}

const InviteMidotUsersModalContent: React.FC<InviteMidotUsersModalContentProps> = ({ onClose, isLoading }) => {
  const { t } = useTranslation(['users', 'common']);
  const { formState } = useFormContext<InviteMidotUsersFormData>();
  const { isValid } = formState;

  return (
    <>
      <Box sx={{ p: 4, pt: 0, '& .MuiFormControl-root': { p: 0, mb: 0 } }}>
        <Stack direction="row" alignItems="center" justifyContent="center" mb={2}>
          <Box component="img" src="/images/invite-midot-users.svg" />
        </Stack>

        <InviteMidotUsersTable />
      </Box>
      <CommonFormFooter
        onReset={onClose}
        resetText={t('common:actions.cancel')}
        submitText={t('users.inviteMidotUsers.invite')}
        disableSubmit={!isValid || isLoading}
      />
    </>
  );
};

const defaultValues: InviteMidotUsersFormData = {
  assignments: [
    {
      role: undefined,
      office: [],
      user: undefined,
      phone: ''
    }
  ]
};

const InviteMidotUsersModal: React.FC<InviteMidotUsersModalProps> = ({ onClose }) => {
  const { t } = useTranslation(['users', 'common']);
  const { showMessage } = useSnackbar();
  const { mutateAsync: inviteMidotUsers, isLoading } = useAssignUsers();

  const validationSchema = useMemo(
    () =>
      yup.object({
        assignments: yup
          .array()
          .of(
            yup.object({
              role: yup.string().min(1).required(),
              office: yup.array().of(yup.string().min(1).required()).min(1).required(),
              user: yup
                .object()
                .shape({
                  id: yup.string(),
                  email: yup.string().email(),
                  phone: yup.string()
                })
                .test(
                  'has-id-or-email',
                  t('users.inviteMidotUsers.validation.userRequired'),
                  value => !!(value?.id || value?.email)
                )
                .test(
                  'allowed-email-domain',
                  t('users.inviteMidotUsers.validation.invalidEmailDomain', {
                    domains: ALLOWED_EMAIL_DOMAINS.join(', ')
                  }),
                  value => {
                    // If user has an id (existing user), skip domain validation
                    if (value?.id) return true;
                    // If user has an email (new invite), check domain
                    if (value?.email) return isAllowedEmailDomain(value.email);

                    // No value yet, validation will be handled by has-id-or-email test
                    return true;
                  }
                ),
              // Phone is required and must be valid when inviting a new user by email
              phone: yup.string().when('user', {
                is: (user: UserIdentifier) => user?.email && !user?.id,
                then: schema =>
                  schema
                    .required()
                    .test('valid-phone', 'Invalid phone number', value => !!value && matchIsValidTel(value)),
                otherwise: schema => schema.nullable()
              })
            })
          )
          .min(1)
          .required()
      }),
    [t]
  );

  const handleCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSend = useCallback(
    async (data: InviteMidotUsersFormData) => {
      try {
        const midotUserAssignmentRequest: MidotUserAssignmentRequest = {
          midotAssignments: data.assignments
            .filter(assignment => assignment.user?.id || assignment.user?.email)
            .map(assignment => {
              const user = assignment.user!;
              const userPayload: UserIdentifier = user.email
                ? { email: user.email, phone: assignment.phone }
                : { id: user.id };

              return {
                users: [userPayload],
                roleIds: assignment.role ? [assignment.role] : [],
                offices: assignment.office
              };
            })
        };

        await inviteMidotUsers({ data: midotUserAssignmentRequest });

        showMessage(t('users.inviteMidotUsers.successMessage'));
      } catch (error) {
        showMessage(t('common:error.somethingWentWrong'));
        throw error;
      }
    },
    [showMessage, t, inviteMidotUsers]
  );

  return (
    <SettingsModalForm
      isOpen
      onClose={handleCloseModal}
      closeOnSave={false}
      onSave={handleSend as (v: FieldValues) => Promise<void>}
      head={
        <Typography component="h2" variant="h3">
          {t('users.inviteMidotUsers.title')}
        </Typography>
      }
      sx={{
        [`& .${paperClasses.root}`]: {
          margin: 0,
          width: 1000,
          height: 'unset'
        }
      }}
      defaultValues={defaultValues}
      validation={validationSchema}
      validationMode="onChange"
    >
      <InviteMidotUsersModalContent onClose={handleCloseModal} isLoading={isLoading} />
    </SettingsModalForm>
  );
};

export default InviteMidotUsersModal;
